import { Grid, LinearProgress, Typography } from "@material-ui/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { MentorTip, QuickwinCard } from "../../../components";
import { FooterActions } from "../../../components/wizard/footer-actions.component";
import { getParticipantPaymentSummary } from "../../../mentor/services";
import { type MxQuickWin, Status } from "../../../models/gameplan";
import useBuySessionPointsModal from "../../../shared/hooks/use-buy-session-points-modal";
import { useHistory } from "../../../shared/hooks/useHistory";
import { getActiveCourse } from "../../../shared/slices/activeCourse";
import {
  type QuickWinApi,
  getQuickwins,
  setQuickWinStatus,
} from "../../learningPath.service";
import { quickwinsStyles } from "./quickwins.styles";

interface QuickWinsProps {
  onQuickWinClick?: (quickWin: MxQuickWin) => void;
}

export const Quickwins = ({ onQuickWinClick }: QuickWinsProps) => {
  const classes = quickwinsStyles();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { buySessionPointsModal, togglePurchaseDialog } =
    useBuySessionPointsModal();
  const { courseId: urlCourseId } = useParams<{ courseId?: string }>();
  const defaultCourseId = useSelector(getActiveCourse);
  const courseId = (urlCourseId || defaultCourseId) as number;

  const { data: quickwins, isFetching } = useQuery({
    queryKey: ["quickwins", courseId],
    queryFn: () => getQuickwins(courseId),
    refetchOnWindowFocus: false,
    enabled: !!courseId,
  });

  const { data: participantPaymentSummary } = useQuery({
    queryKey: ["participant-payment-profile"],
    queryFn: getParticipantPaymentSummary,
    refetchOnWindowFocus: false,
  });

  const { mutate } = useMutation(setQuickWinStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(["quickwins"]);
    },
  });

  const getSessionPointsFromProfileSummary = (): number => {
    const serviceCredits =
      participantPaymentSummary?.creditSummary?.serviceCredits;

    if (serviceCredits) {
      return (
        serviceCredits.find((service) => service.name === "Session Point")
          ?.availableAmount ?? 0
      );
    }

    return 0;
  };

  const sessionPoints = getSessionPointsFromProfileSummary();

  const onClick = (quickWin: QuickWinApi) => {
    if (quickWin.status === Status.NEW && quickWin.actionStep !== "OneOnOne")
      mutate({ status: Status.IN_PROGRESS, id: quickWin.id });

    if (
      quickWin.status === Status.NEW &&
      quickWin.actionStep === "OneOnOne" &&
      sessionPoints >= 1
    )
      mutate({ status: Status.IN_PROGRESS, id: quickWin.id });

    if (quickWin.actionStep === "OneOnOne" && sessionPoints <= 0) {
      return togglePurchaseDialog();
    }
    if (
      quickWin.actionStep === "OneOnOne" &&
      quickWin.status === Status.COMPLETED
    ) {
      return;
    }
    onQuickWinClick({
      quickWin: quickWin.actionStep,
      id: quickWin.id,
    });
  };

  const handleNextAction = () => {
    history.push("/participant/learning");
  };

  return (
    <>
      {isFetching ? (
        <div data-testid="loading">
          <LinearProgress color="secondary" />
        </div>
      ) : (
        <Grid container spacing={3}>
          <Typography variant="h6" className="title">
            Quick Wins
          </Typography>
          <MentorTip
            className={classes.mentorTip}
            title={
              <Typography>
                <b>Quick Wins </b> are tasks within your Game Plan to help you
                reach your main goal through actionable steps.
              </Typography>
            }
          />
          {quickwins?.map((quickWin) => (
            <Grid
              className={clsx({
                [classes.clickableItem]: quickWin.actionStep,
              })}
              item
              xs={4}
              key={quickWin.id}
              onClick={() => onClick(quickWin)}
            >
              <QuickwinCard quickwin={quickWin} />
            </Grid>
          ))}
        </Grid>
      )}
      <FooterActions
        nextText="Finish"
        nextAction={handleNextAction}
        nextForceEnabled
      />
      {buySessionPointsModal}
    </>
  );
};
